"use client";

import * as React from "react";

// Context for dropdown state management
const DropdownMenuContext = React.createContext<{
  open: boolean;
  setOpen: (open: boolean) => void;
}>({
  open: false,
  setOpen: () => {},
});

// Simple implementation without Radix UI dependencies
function DropdownMenu({ children, ...props }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  
  return (
    <DropdownMenuContext.Provider value={{ open, setOpen }}>
      <div data-slot="dropdown-menu" {...props}>{children}</div>
    </DropdownMenuContext.Provider>
  );
}

function DropdownMenuTrigger({ 
  children, 
  asChild,
  ...props 
}: { 
  children: React.ReactNode;
  asChild?: boolean;
  [key: string]: any;
}) {
  const { open, setOpen } = React.useContext(DropdownMenuContext);
  
  const handleClick = () => {
    setOpen(!open);
  };

  if (asChild) {
    // If asChild is true, clone the child element and add our props
    return React.cloneElement(children as React.ReactElement, {
      onClick: handleClick,
      'data-slot': 'dropdown-menu-trigger',
      ...props
    });
  }

  return (
    <button 
      data-slot="dropdown-menu-trigger" 
      onClick={handleClick}
      {...props}
    >
      {children}
    </button>
  );
}

function DropdownMenuContent({ 
  children, 
  className = "",
  align = "start",
  ...props 
}: { 
  children: React.ReactNode;
  className?: string;
  align?: "start" | "end";
  [key: string]: any;
}) {
  const { open, setOpen } = React.useContext(DropdownMenuContext);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('[data-slot="dropdown-menu"]')) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open, setOpen]);

  if (!open) return null;

  const alignClass = align === "end" ? "right-0" : "left-0";

  return (
    <div
      data-slot="dropdown-menu-content"
      className={`absolute ${alignClass} mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-1 z-50 min-w-[8rem] ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

function DropdownMenuItem({ 
  children, 
  className = "",
  onClick,
  ...props 
}: { 
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  [key: string]: any;
}) {
  const { setOpen } = React.useContext(DropdownMenuContext);

  const handleClick = () => {
    onClick?.();
    setOpen(false);
  };

  return (
    <div
      data-slot="dropdown-menu-item"
      className={`px-2 py-1.5 text-sm cursor-pointer hover:bg-gray-100 rounded-sm ${className}`}
      onClick={handleClick}
      {...props}
    >
      {children}
    </div>
  );
}

function DropdownMenuSeparator({ className = "", ...props }: { className?: string; [key: string]: any }) {
  return (
    <div 
      data-slot="dropdown-menu-separator"
      className={`h-px bg-gray-200 my-1 -mx-1 ${className}`}
      {...props}
    />
  );
}

function DropdownMenuLabel({ 
  children, 
  className = "",
  ...props 
}: { 
  children: React.ReactNode;
  className?: string;
  [key: string]: any;
}) {
  return (
    <div
      data-slot="dropdown-menu-label"
      className={`px-2 py-1.5 text-sm font-medium text-gray-700 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

// Simplified versions of other components
function DropdownMenuPortal({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function DropdownMenuGroup({ children }: { children: React.ReactNode }) {
  return <div data-slot="dropdown-menu-group">{children}</div>;
}

function DropdownMenuCheckboxItem({ 
  children, 
  checked,
  onCheckedChange,
  className = "",
  ...props 
}: { 
  children: React.ReactNode;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  className?: string;
  [key: string]: any;
}) {
  const { setOpen } = React.useContext(DropdownMenuContext);

  const handleClick = () => {
    onCheckedChange?.(!checked);
    setOpen(false);
  };

  return (
    <div
      data-slot="dropdown-menu-checkbox-item"
      className={`px-2 py-1.5 text-sm cursor-pointer hover:bg-gray-100 rounded-sm flex items-center gap-2 ${className}`}
      onClick={handleClick}
      {...props}
    >
      <span className="w-4 h-4 border border-gray-300 rounded flex items-center justify-center">
        {checked && <span className="text-xs">✓</span>}
      </span>
      {children}
    </div>
  );
}

function DropdownMenuRadioGroup({ children }: { children: React.ReactNode }) {
  return <div data-slot="dropdown-menu-radio-group">{children}</div>;
}

function DropdownMenuRadioItem({ 
  children, 
  value,
  className = "",
  ...props 
}: { 
  children: React.ReactNode;
  value?: string;
  className?: string;
  [key: string]: any;
}) {
  return (
    <div
      data-slot="dropdown-menu-radio-item"
      className={`px-2 py-1.5 text-sm cursor-pointer hover:bg-gray-100 rounded-sm ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

function DropdownMenuShortcut({ 
  children, 
  className = "",
  ...props 
}: { 
  children: React.ReactNode;
  className?: string;
  [key: string]: any;
}) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={`ml-auto text-xs text-gray-500 ${className}`}
      {...props}
    >
      {children}
    </span>
  );
}

function DropdownMenuSub({ children }: { children: React.ReactNode }) {
  return <div data-slot="dropdown-menu-sub">{children}</div>;
}

function DropdownMenuSubTrigger({ 
  children, 
  className = "",
  ...props 
}: { 
  children: React.ReactNode;
  className?: string;
  [key: string]: any;
}) {
  return (
    <div
      data-slot="dropdown-menu-sub-trigger"
      className={`px-2 py-1.5 text-sm cursor-pointer hover:bg-gray-100 rounded-sm ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

function DropdownMenuSubContent({ 
  children, 
  className = "",
  ...props 
}: { 
  children: React.ReactNode;
  className?: string;
  [key: string]: any;
}) {
  return (
    <div
      data-slot="dropdown-menu-sub-content"
      className={`bg-white border border-gray-200 rounded-md shadow-lg p-1 z-50 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

export {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
};